<script setup>
import Modal from "@/components/Tools/Modal.vue";
import { computed, defineEmits, defineProps } from "vue";
import { useNavigationEvent } from "@/components/ExternalApps/composables/useNavigationEvent";

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  legacyChannelId: {
    type: [Number, String],
    default: null
  },
  // idle | loading | active | inactive | error — result of the proactive
  // legacy sales channel check; confirm is only enabled on "inactive"
  channelCheck: {
    type: String,
    default: "idle"
  }
});

const emits = defineEmits(["confirm", "close"]);

const checkLoading = computed(() => props.channelCheck === "loading");
const confirmDisabled = computed(() => props.channelCheck !== "inactive");

const { navigate } = useNavigationEvent();

const showDialog = computed({
  get() {
    return props.show;
  },
  set(newValue) {
    if (newValue) {
      return;
    }
    emits("close");
  }
});

function openLegacyShopImporter() {
  if (!props.legacyChannelId) {
    return;
  }

  navigate(
    "/index.php",
    {
      module: "onlineshops",
      action: "edit",
      id: props.legacyChannelId
    },
    "_blank"
  );
}
</script>

<template>
  <Modal
    :show-modal="showDialog"
    width="500"
    :title="
      $t('salesChannelManagementApp.amazon.activateReceiptTitle').toString()
    "
    @toggle-modal="$emit('close')"
  >
    <template #content>
      <p class="receipt-intro">
        {{ $t("salesChannelManagementApp.amazon.activateReceiptIntro") }}
      </p>

      <div class="receipt-warning">
        <i class="fal fa-exclamation-triangle receipt-warning__icon" />
        <p class="receipt-warning__text">
          <strong>{{
            $t("salesChannelManagementApp.amazon.activateReceiptConfirm")
          }}</strong>
          {{ $t("salesChannelManagementApp.amazon.activateReceiptWarning") }}
        </p>
      </div>

      <p class="receipt-instruction">
        {{ $t("salesChannelManagementApp.amazon.activateReceiptInstruction") }}
      </p>

      <div class="receipt-links">
        <a
          v-if="legacyChannelId"
          class="receipt-links__item"
          href="#"
          @click.prevent="openLegacyShopImporter"
        >
          <i class="fal fa-arrow-up-right-from-square receipt-links__icon" />
          {{ $t("salesChannelManagementApp.amazon.activateReceiptOpenLegacy") }}
        </a>
        <a
          class="receipt-links__item"
          :href="
            $t(
              'salesChannelManagementApp.amazon.activateReceiptHandbookUrl'
            ).toString()
          "
          target="_blank"
          rel="noopener noreferrer"
        >
          <i class="fal fa-book receipt-links__icon" />
          {{
            $t("salesChannelManagementApp.amazon.activateReceiptOpenHandbook")
          }}
        </a>
      </div>

      <div v-if="checkLoading" class="receipt-check receipt-check--loading">
        <i class="fal fa-spinner-third fa-spin" />
        {{ $t("salesChannelManagementApp.amazon.activateReceiptChecking") }}
      </div>
      <div
        v-else-if="channelCheck === 'active'"
        class="receipt-check receipt-check--blocked"
      >
        <i class="fal fa-circle-exclamation" />
        {{
          $t(
            "salesChannelManagementApp.amazon.activateReceiptLegacyStillActive"
          )
        }}
      </div>
      <div
        v-else-if="channelCheck === 'error'"
        class="receipt-check receipt-check--blocked"
      >
        <i class="fal fa-circle-exclamation" />
        {{ $t("salesChannelManagementApp.amazon.activateReceiptCheckFailed") }}
      </div>
    </template>
    <template #footer>
      <div class="d-flex justify-content-end">
        <button
          class="btn btn-primary"
          :disabled="confirmDisabled"
          @click="$emit('confirm')"
        >
          <i v-if="checkLoading" class="fal fa-spinner-third fa-spin mr-2" />
          {{ $t("general.confirm") }}
        </button>
        <button class="btn btn-outline-primary ml-2" @click="$emit('close')">
          {{ $t("general.cancel") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.receipt-intro {
  margin-bottom: 1rem;
  color: var(--ax-fg-secondary, #475467);
}

.receipt-warning {
  display: flex;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  margin-bottom: 1.25rem;
  border: 1px solid var(--ax-border-warning, #fec84b);
  border-radius: 8px;
  background: var(--ax-bg-warning-subtle, #fffaeb);

  &__icon {
    flex-shrink: 0;
    margin-top: 0.15rem;
    color: var(--ax-fg-warning, #dc6803);
  }

  &__text {
    margin: 0;
    color: var(--ax-fg-primary, #344054);

    strong {
      font-weight: 600;
    }
  }
}

.receipt-instruction {
  margin-bottom: 0.75rem;
  color: var(--ax-fg-secondary, #475467);
}

.receipt-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  &__item {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.875rem;
    border: 1px solid var(--ax-border-primary, #d0d5dd);
    border-radius: 8px;
    color: var(--ax-fg-brand-primary, #{$color-connect-primary});
    text-decoration: none;
    font-weight: 600;

    &:hover {
      border-color: var(--ax-fg-brand-primary, #{$color-connect-primary});
      background: var(--ax-bg-brand-subtle, #f5f6ff);
      text-decoration: none;
    }
  }

  &__icon {
    flex-shrink: 0;
    font-size: 0.9em;
  }
}

.receipt-check {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;

  &--loading {
    color: var(--ax-fg-secondary, #475467);
    background: var(--ax-bg-secondary, #f2f4f7);
  }

  &--blocked {
    color: var(--ax-fg-error, #b42318);
    background: var(--ax-bg-error-subtle, #fef3f2);
    border: 1px solid var(--ax-border-error, #fda29b);
  }
}
</style>
